import { GLOBAL_NEWS_PROFILE_ID } from "@akem/shared";
import { useCanvasStore } from "../stores/canvasStore.js";
import type { Position, Size } from "../types/index.js";

export const MARKET_CANVAS_KEY = "market:overview";
export const MARKET_CANVAS_NAME = "Market Overview";

const MARKET_INTELLIGENCE_PROFILE_ID = GLOBAL_NEWS_PROFILE_ID;

const GRID_GAP = 40;
const START_X = 40;
const START_Y = 40;
const PRIMARY_COLUMN_WIDTH = 800;
const SECONDARY_COLUMN_WIDTH = 520;
const SECONDARY_COLUMN_X = START_X + PRIMARY_COLUMN_WIDTH + GRID_GAP;

const CALENDAR_HEIGHT = 280;
const TICKER_HEIGHT = 280;
const INDICES_HEIGHT = 230;
const SECTOR_HEIGHT = 230;
const FIRST_ROW_HEIGHT = Math.max(CALENDAR_HEIGHT, TICKER_HEIGHT);
const SECOND_ROW_HEIGHT = Math.max(INDICES_HEIGHT, SECTOR_HEIGHT);

const BASE_WIDGET_LAYOUTS: Array<{
  type: string;
  position: Position;
  size?: Size;
  config?: Record<string, any>;
}> = [
  {
    type: "calendar",
    position: { x: START_X, y: START_Y },
    size: { width: PRIMARY_COLUMN_WIDTH, height: CALENDAR_HEIGHT },
    config: { title: "Market Calendar" },
  },
  {
    type: "akemTicker",
    position: { x: SECONDARY_COLUMN_X, y: START_Y },
    size: { width: SECONDARY_COLUMN_WIDTH, height: TICKER_HEIGHT },
    config: { title: "Akem Sauce" },
  },
  {
    type: "indicesOverview",
    position: { x: START_X, y: START_Y + FIRST_ROW_HEIGHT + GRID_GAP },
    size: { width: PRIMARY_COLUMN_WIDTH, height: INDICES_HEIGHT },
    config: { title: "Indices Overview" },
  },
  {
    type: "sectorOverview",
    position: {
      x: SECONDARY_COLUMN_X,
      y: START_Y + FIRST_ROW_HEIGHT + GRID_GAP,
    },
    size: { width: SECONDARY_COLUMN_WIDTH, height: SECTOR_HEIGHT },
    config: { title: "Sectors Overview" },
  },
];

const SECOND_ORDER_DOMAINS = [
  { id: "customers", label: "Customers" },
  { id: "suppliers", label: "Suppliers" },
  { id: "marketTrends", label: "Market Trends" },
  { id: "politics", label: "Politics" },
  { id: "regulatory", label: "Regulatory" },
  { id: "mna", label: "M&A" },
  { id: "logistics", label: "Logistics" },
  { id: "themes", label: "Themes" },
] as const;

const SECOND_ORDER_CARD_SIZE: Size = { width: 320, height: 360 };
const SECOND_ORDER_COLUMNS = 4;
const SECOND_ORDER_GAP = 24;
// Start the summary grid below the core overview rows
const SECOND_ORDER_START: Position = {
  x: START_X,
  y:
    START_Y +
    FIRST_ROW_HEIGHT +
    GRID_GAP +
    SECOND_ROW_HEIGHT +
    GRID_GAP,
};

function ensureLayout(canvasId: string) {
  const store = useCanvasStore.getState();
  const canvas = store.canvases.find((c) => c.id === canvasId);

  // Helper to test if a widget of a type exists
  const hasType = (type: string) =>
    Boolean(canvas?.widgets.some((w) => w.type === type));

  // Helper to normalize a widget's config for Market (must not carry a profileId)
  // Important: only patch the keys we intend to change so we don't clobber
  // any updates performed earlier in this reconciliation pass (e.g. titles).
  const normalizeMarketConfig = (
    wType: string,
    resetOutputs?: string[],
    forcedOverrides?: Record<string, any>
  ) => {
    // Always read the latest canvas snapshot to avoid stale configs
    const current = useCanvasStore
      .getState()
      .canvases.find((c) => c.id === canvasId);
    const widgets = (current?.widgets || []).filter((w) => w.type === wType);

    widgets.forEach((w) => {
      const cfg: any = w.config || {};
      if (forcedOverrides) {
        // Only apply the forced keys, do not spread stale cfg
        store.updateWidgetConfig(canvasId, w.id, forcedOverrides);
      } else if (cfg.profileId) {
        // Explicitly clear profileId without touching other fields
        store.updateWidgetConfig(canvasId, w.id, { profileId: undefined });
      }
      if (Array.isArray(resetOutputs) && resetOutputs.length > 0) {
        const reset: Record<string, any> = {};
        resetOutputs.forEach((id) => (reset[id] = null));
        store.updateWidgetOutputs(canvasId, w.id, reset);
      }
    });
  };

  // Ensure core overview widgets (one of each)
  BASE_WIDGET_LAYOUTS.forEach((layout) => {
    if (!hasType(layout.type)) {
      const widgetId = store.addWidgetToCanvas(
        canvasId,
        layout.type,
        layout.position,
        layout.config
      );
      if (layout.size) store.resizeWidget(canvasId, widgetId, layout.size);
    }
  });

  // Ensure 8 Akem Intelligence domain cards (dedupe by domain)
  SECOND_ORDER_DOMAINS.forEach((domain, index) => {
    const existing = canvas?.widgets.find(
      (w) =>
        w.type === "akemIntelligence" &&
        (w.config as any)?.domain === domain.id
    );
    if (existing) {
      const cfg = (existing.config || {}) as Record<string, any>;
      if (cfg.title !== domain.label) {
        // Only update the title to avoid reapplying stale config values
        store.updateWidgetConfig(canvasId, existing.id, { title: domain.label });
      }
    } else {
      const column = index % SECOND_ORDER_COLUMNS;
      const row = Math.floor(index / SECOND_ORDER_COLUMNS);
      const position = {
        x:
          SECOND_ORDER_START.x +
          column * (SECOND_ORDER_CARD_SIZE.width + SECOND_ORDER_GAP),
        y:
          SECOND_ORDER_START.y +
          row * (SECOND_ORDER_CARD_SIZE.height + SECOND_ORDER_GAP),
      };
      const widgetId = store.addWidgetToCanvas(
        canvasId,
        "akemIntelligence",
        position,
        {
          title: domain.label,
          domain: domain.id,
          profileId: MARKET_INTELLIGENCE_PROFILE_ID,
        }
      );
      store.resizeWidget(canvasId, widgetId, SECOND_ORDER_CARD_SIZE);
    }
  });

  // Normalize any existing core widgets so they are truly market-scoped
  normalizeMarketConfig("akemTicker", ["highlightSelected"]);
  normalizeMarketConfig("calendar", ["selectedEvent"]);
  normalizeMarketConfig("akemIntelligence", undefined, {
    profileId: MARKET_INTELLIGENCE_PROFILE_ID,
  });

  // Strict reconciliation: remove any widgets that do not belong to the Market preset
  try {
    const allowedSingleTypes = new Set<string>([
      "calendar",
      "akemTicker",
      "indicesOverview",
      "sectorOverview",
    ]);
    const allowedDomains = new Set(SECOND_ORDER_DOMAINS.map((d) => d.id));
    const keepIds = new Set<string>();

    // Keep only one of each single-type widget (prefer first encountered)
    const current = useCanvasStore
      .getState()
      .canvases.find((c) => c.id === canvasId);
    if (current) {
      const seenSingles = new Set<string>();
      for (const w of current.widgets) {
        if (allowedSingleTypes.has(w.type)) {
          if (!seenSingles.has(w.type)) {
            seenSingles.add(w.type);
            keepIds.add(w.id);
          }
        }
      }

      // Keep one Akem Intelligence widget per allowed domain
      const seenDomains = new Set<string>();
      for (const w of current.widgets) {
        if (w.type === "akemIntelligence") {
          const d = (w.config as any)?.domain;
          if (d && allowedDomains.has(d) && !seenDomains.has(d)) {
            seenDomains.add(d);
            keepIds.add(w.id);
          }
        }
      }

      // Remove everything else (including duplicates, foreign types, profile-scoped widgets, run headers)
      const toRemove = current.widgets
        .filter((w) => !keepIds.has(w.id))
        .map((w) => w.id);
      toRemove.forEach((wid) => {
        try {
          store.removeWidget(canvasId, wid);
        } catch {}
      });

      // Clean up connections that reference removed widgets
      const refreshed = useCanvasStore
        .getState()
        .canvases.find((c) => c.id === canvasId);
      if (refreshed) {
        const validIds = new Set(refreshed.widgets.map((w) => w.id));
        const dangling = refreshed.connections.filter(
          (c) =>
            !validIds.has(c.source.widgetId) || !validIds.has(c.target.widgetId)
        );
        dangling.forEach((conn) => {
          try {
            store.removeConnection(canvasId, conn.id);
          } catch {}
        });
      }

      // Remove any synthetic group widgets to keep layout simple
      try {
        const latest = useCanvasStore
          .getState()
          .canvases.find((c) => c.id === canvasId);
        latest?.widgets
          .filter((w) => w.type === "widgetGroup")
          .forEach((w) => {
            try {
              store.removeWidget(canvasId, w.id);
            } catch {}
          });
      } catch {}
    }
  } catch {}

  // After reconciliation, enforce canonical positions/sizes for the primary rows
  try {
    const latest = useCanvasStore
      .getState()
      .canvases.find((c) => c.id === canvasId);
    if (latest) {
      const byType = (t: string) => latest.widgets.find((w) => w.type === t);
      for (const layout of BASE_WIDGET_LAYOUTS) {
        const w = byType(layout.type);
        if (!w) continue;
        // Move and resize to canonical layout
        if (layout.position) store.moveWidget(canvasId, w.id, layout.position);
        if (layout.size) store.resizeWidget(canvasId, w.id, layout.size);
      }

      // Reflow intelligence cards into the grid starting below portfolios
      const seenDomains = new Set<string>();
      let index = 0;
      const columns = SECOND_ORDER_COLUMNS; // Keep canonical grid across hydration + reconciliation
      for (const domain of SECOND_ORDER_DOMAINS) {
        const w = latest.widgets.find(
          (w) =>
            w.type === "akemIntelligence" && (w.config as any)?.domain === domain.id
        );
        if (!w) continue;
        const column = index % columns;
        const row = Math.floor(index / columns);
        const position = {
          x:
            SECOND_ORDER_START.x +
            column * (SECOND_ORDER_CARD_SIZE.width + SECOND_ORDER_GAP),
          y:
            SECOND_ORDER_START.y +
            row * (SECOND_ORDER_CARD_SIZE.height + SECOND_ORDER_GAP),
        };
        store.moveWidget(canvasId, w.id, position);
        store.resizeWidget(canvasId, w.id, SECOND_ORDER_CARD_SIZE);
        seenDomains.add(domain.id);
        index += 1;
      }
    }
  } catch {}
}

type CanvasStoreSnapshot = ReturnType<typeof useCanvasStore.getState>;

/**
 * Ensures the market overview canvas exists (idempotent) and returns its id.
 */
export const ensureMarketOverviewCanvas = (
  store: CanvasStoreSnapshot
): string => {
  const existing = store.canvases.find(
    (canvas) =>
      canvas.key === MARKET_CANVAS_KEY ||
      canvas.kind === "market" ||
      canvas.name === MARKET_CANVAS_NAME
  );

  if (existing) {
    if (
      existing.name !== MARKET_CANVAS_NAME ||
      existing.kind !== "market" ||
      existing.key !== MARKET_CANVAS_KEY
    ) {
      store.setCanvasIdentity(existing.id, {
        name: MARKET_CANVAS_NAME,
        kind: "market",
        key: MARKET_CANVAS_KEY,
      });
    }
    store.setActiveCanvas(existing.id);
    // Ensure required widgets exist on the market canvas (idempotent)
    ensureLayout(existing.id);
    return existing.id;
  }

  const canvasId = store.createCanvas(MARKET_CANVAS_NAME, {
    kind: "market",
    key: MARKET_CANVAS_KEY,
  });

  store.setActiveCanvas(canvasId);
  ensureLayout(canvasId);

  return canvasId;
};
